import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import { getBilling } from "@/lib/billing";
import { usePremium } from "@/lib/premium";
import { track } from "@/lib/analytics";
import { TOTAL_INTERVENTION_COUNT } from "@/lib/resetData";

const VALUE_PROPS = [
  `The full reset library — all ${TOTAL_INTERVENTION_COUNT} guided techniques`,
  "Unlimited Toolkit — keep everything that works for you",
  "Patterns over time — what you saved, what you used, and when",
  "Living Book — guided pages for people, wishes, practical information and words to keep",
];

export default function Premium() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { premium, refresh } = usePremium();
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const billing = getBilling();

  useEffect(() => {
    track("paywall_viewed", { source: params.get("source") || "direct", platform: billing.platform });
    (async () => {
      try {
        await billing.init();
        const p = await billing.getPlans();
        setPlans(p);
        setSelected(p.find((x) => x.period === "annual") || p[0] || null);
      } catch (e) {
        console.warn("[premium] init:", e?.message || e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const purchase = async () => {
    if (!selected) return;
    setBusy(true);
    setMessage("");
    track("subscription_started", { plan: selected.id, platform: billing.platform });
    const res = await billing.purchase(selected);
    setBusy(false);
    if (res.ok && res.premium) {
      track("subscription_activated", { plan: selected.id, platform: billing.platform });
      await refresh();
      toast("Premium is active.", { description: "Your additional tools and options are ready." });
    } else if (res.code === "WEB_UNAVAILABLE") {
      setMessage(res.message);
    } else if (res.code !== "CANCELLED") {
      setMessage(res.message || "We couldn't confirm access. Check Google Play or restore purchases before trying again.");
    }
  };

  const restore = async () => {
    setBusy(true);
    setMessage("");
    const res = await billing.restore();
    setBusy(false);
    if (res.ok) {
      await refresh();
      toast(res.premium ? "Premium restored." : "No previous purchase found.");
    } else if (res.code === "WEB_UNAVAILABLE") {
      setMessage(res.message);
    } else {
      setMessage(res.message);
    }
  };

  const simulate = async () => {
    const res = await billing.simulatePurchase();
    if (res.ok) {
      track("subscription_activated", { plan: "simulated", platform: "web-preview" });
      await refresh();
      toast("Preview: Premium simulated.", { description: "This only works in the preview environment." });
    }
  };

  return (
    <PageContainer testid="premium-page" className="pt-8">
      <button
        data-testid="premium-back-btn"
        onClick={() => nav(-1)}
        className="inline-flex items-center gap-1.5 text-[13px] un-press mb-6"
        style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
      >
        <ArrowLeft size={14} /> Back
      </button>

      {premium ? (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="premium-active-state">
          <div
            className="mx-auto rounded-full flex items-center justify-center mb-6"
            style={{
              width: 60,
              height: 60,
              background: "radial-gradient(circle at 35% 30%, #F7ECD3 0%, #D6B884 55%, #C4A16A 100%)",
              boxShadow: "0 0 26px rgba(214,184,132,0.4)",
            }}
          >
            <Check size={24} style={{ color: "#2A1B2A" }} />
          </div>
          <h1 className="text-center text-[28px]" style={{ color: "var(--un-text-1)", fontFamily: "'Lora', serif", fontWeight: 500 }}>
            Premium is active
          </h1>
          <p className="mt-3 text-center text-[14px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
            The full technique library, unlimited Toolkit, and pattern views are available.
            Manage recurring subscriptions in Google Play. One-time purchase options, when offered, do not renew.
          </p>
          {billing.canSimulate && (
            <button
              data-testid="premium-simulate-clear"
              onClick={async () => {
                await billing.simulateClear();
                await refresh();
              }}
              className="mt-8 mx-auto block text-[12px] underline un-press"
              style={{ color: "var(--un-text-3)" }}
            >
              Preview only: clear simulated subscription
            </button>
          )}
        </motion.div>
      ) : (
        <>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <h1
              className="text-[30px] leading-tight"
              style={{ color: "var(--un-text-1)", fontFamily: "'Lora', serif", fontWeight: 500 }}
            >
              More ways to reflect,
              <br />
              <span className="italic" style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--un-burgundy)", fontWeight: 400 }}>
                at your own pace.
              </span>
            </h1>
            <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
              Everything you already use stays free — the check-in, Zen,
              Journal, Safe Reach, and your private spaces. Premium adds more tools and options.
            </p>
          </motion.div>

          <div className="mt-6 flex flex-col gap-3">
            {VALUE_PROPS.map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 * i }}
                className="flex items-start gap-3"
              >
                <Sparkles size={14} className="mt-0.5 shrink-0" style={{ color: "#C4A16A" }} />
                <p className="text-[14px] leading-relaxed" style={{ color: "var(--un-text-2)" }}>
                  {v}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-3">
            {plans.map((p) => (
              <button
                key={p._pkg?.identifier || p.id}
                data-testid={`premium-plan-${p.period}`}
                onClick={() => setSelected(p)}
                aria-pressed={selected === p}
                disabled={busy}
                className="un-press p-4 rounded-3xl flex items-center justify-between text-left"
                style={{
                  background: selected === p ? "rgba(196,161,106,0.16)" : "var(--un-bg-2)",
                  border: selected === p ? "1.5px solid #C4A16A" : "1.5px solid transparent",
                }}
              >
                <div>
                  <p className="text-[15px] font-medium" style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}>
                    {p.title}
                  </p>
                  {p.note && (
                    <p className="text-[12px] mt-0.5" style={{ color: "var(--un-text-3)" }}>
                      {p.note}
                    </p>
                  )}
                </div>
                <span className="text-[14px]" style={{ color: "var(--un-text-2)", fontFamily: "Manrope, sans-serif" }}>
                  {p.priceString}
                </span>
              </button>
            ))}
            {plans.length === 0 && (
              <p data-testid="premium-no-plans" className="text-[13px] text-center py-4" style={{ color: "var(--un-text-3)" }}>
                {billing.platform === "web" ? "Purchases are available in the Android app. Available options and localized prices are shown there before purchase." : "Plans are unavailable right now. Please try again in a moment."}
              </p>
            )}
          </div>

          <button
            data-testid="premium-subscribe-btn"
            onClick={purchase}
            disabled={busy || !selected}
            className="mt-5 w-full py-4 rounded-full text-[15px] font-medium un-press disabled:opacity-60"
            style={{
              background: "var(--un-plum)",
              color: "#FDFBF7",
              fontFamily: "Manrope, sans-serif",
              boxShadow: "0 12px 28px rgba(74,32,64,0.20)",
            }}
          >
            {busy ? "One moment…" : "Continue"}
          </button>

          {message && (
            <p data-testid="premium-message" role="alert" className="mt-3 text-center text-[13px]" style={{ color: "var(--un-burgundy)" }}>
              {message}
            </p>
          )}

          <button
            data-testid="premium-restore-btn"
            onClick={restore}
            disabled={busy}
            className="mt-4 mx-auto block text-[13px] underline underline-offset-2 un-press"
            style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
          >
            Restore purchases
          </button>

          {billing.canSimulate && (
            <button
              data-testid="premium-simulate-btn"
              onClick={simulate}
              className="mt-3 mx-auto block text-[12px] underline un-press"
              style={{ color: "var(--un-text-3)" }}
            >
              Preview only: simulate subscription
            </button>
          )}

          <p className="mt-7 mb-10 text-center text-[11px] leading-relaxed px-4" style={{ color: "var(--un-text-3)" }}>
            {selected?.period === "lifetime" ? "Lifetime is a one-time purchase and does not renew." : "Monthly and Annual auto-renew until canceled. Manage subscriptions in Google Play."}
            Crisis support and essential Immediate Reset options are never behind this
            subscription.{" "}
            <a href="/terms" className="underline">Terms</a> ·{" "}
            <a href="/privacy" className="underline">Privacy</a>
          </p>
        </>
      )}
    </PageContainer>
  );
}
